import { svgLoading } from 'stories/assets'
import type { Color } from 'stories/types'

interface ButtonProps {
  type?: 'button' | 'submit'
  /**
   * What background color to use
   */
  color?: Color | string
  /**
   * How large should the button be?
   */
  size?: string
  /**
   * Is full rounded button
   */
  pills?: boolean
  /**
   * Is outlined button
   */
  outline?: boolean
  /**
   * Is link button
   */
  link?: boolean
  /**
   * Is bold text
   */
  bold?: boolean
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is loading
   */
  loading?: boolean
  /**
   * Custom class name
   */
  className?: string
  /**
   * Button contents
   */
  children: JSX.Element | string
  /**
   * Optional click handler
   */
  onClick?: () => void

  [key: string]: any
}

/**
 * Primary UI component for user interaction
 */
export const Button = ({
  type = 'button',
  size = '[15.5px]',
  color = 'primary',
  pills = false,
  outline = false,
  link = false,
  bold = false,
  disabled = false,
  loading = false,
  full = false,
  children,
  className = '',
  ...props
}: ButtonProps) => {
  if (loading) disabled = true
  if (color === 'white') color = 'gray'
  if (['primary', 'shade-blue'].includes(color)) {
    const colorLevel = disabled ? 60 : 100
    color = `${color}/${colorLevel}`
  } else {
    const colorLevel = disabled ? 300 : 500
    color = `${color}-${colorLevel}`
  }

  const classes = [
    link ? '' : `border border-${color}`,
    link ? `text-${color}` : outline ? `text-${color}` : color === 'white' ? 'text-gray-900' : 'text-white',
    link ? '' : outline ? `hover:text-white focus:outline-none` : `bg-${color}`, //`bg-primary`,
    link ? `block mt-3 hover:text-gray-900 hover:underline text-gray-700` : '',
    // disabled ? '' : `hover:bg-${color}-600`,
    disabled && 'cursor-not-allowed',
    disabled || link ? '' : !outline ? `hover:bg-white hover:text-${color}` : `hover:bg-${color}`,
    link ? '' : 'focus:ring-4',
    link ? '' : `focus:ring-${color}/300`,
    `font-${size}`,
    `rounded-${pills ? 'full' : 'lg'}`,
    `text-${size}`,
    bold ? 'font-bold' : '',
    full ? 'w-full' : '',
    link ? '' : props.noPadding ? '' : 'px-5',
    props.noPadding ? '' : 'py-2.5',
    'mr-2',
    'mb-2',
    `dark:bg-${color}/60`,
    `dark:hover:bg-${color}`,
    'focus:outline-none',
    `dark:focus:ring-${color}/80`,
    loading ? 'inline-flex' : '',
    'items-center justify-center',
    ...className.split(' '),
    'transition-all duration-150',
  ]

  return (
    <button type={type} className={classes.join(' ')} disabled={disabled} {...props}>
      {loading && <img src={svgLoading} className="inline w-4 h-4 mr-3 text-white animate-spin" />}
      <span>{children}</span>
    </button>
  )
}
